import { Component, type ReactNode } from "react";
import { EmptyState, MEyebrow, Skeleton } from "@cntrl/ui";
import { useBandContext } from "../../../app/bandContext.js";
import { useNavigation } from "../../../app/hooks/useSession.js";
import {
  GreetingCard,
  ShowtimeCard,
  RecentReleaseCard,
  OpportunitiesCard,
  TodoCard,
  UpcomingCard,
  TravelCard,
  TicketSalesCard,
  VipCard,
  CaseChecklistCard,
} from "./homeCards.js";
import { TrendingHomeCard } from "./TrendingHomeCard.js";
import { AudienceCard } from "./AudienceCard.js";
import { MerchSectionCard, MerchPosSectionCard } from "./MerchSection.js";
import { MyBunkCard } from "../../buses/MyBunkCard.js";
import styles from "./MobileHome.module.scss";

/** PRO My Bunk (ART-147 §7.5) — the Home travel row's bunk. `hide` when not bunked: an empty "no bus" row on
 *  every non-touring day is noise. The server adds `my_bunk` to the stack only for a Pro band. */
const MyBunkHomeCard = () => <MyBunkCard emptyMode="hide" />;

/**
 * The role-keyed mobile HOME widget stack (MOBILE Home maps). The server nav
 * document owns the per-role widget-key LIST (`nav.mobile.home`, computed by `BuildBandHome`) — this
 * screen holds NO role→widget matrix, it just resolves each key to a card via {@link HOME_WIDGETS} and stacks them.
 * Each card owns its own data hook + four-state contract; each is wrapped in a {@link WidgetBoundary} so one card's
 * render crash degrades to a single error tile, never a blank home (per-widget boundaries). Live updates ride
 * the `band:{id}` / `user:{id}` groups the MobileShell already joined + the shared invalidation registry — the cards
 * reuse the desktop hooks, so `sync-completed` / `schedule-changed` / `opportunity-changed` / `task-changed`
 * repaint them with no extra wiring; the VIP card additionally joins its `show:{id}` group.
 */
const HOME_WIDGETS: Record<string, () => ReactNode> = {
  greeting: GreetingCard,
  // Trending LEADS the stacks that carry it (ART-176). Renders NOTHING when nothing is trending — see
  // TrendingHomeCard — so it is only ever a lead card when there is something to say.
  trending: TrendingHomeCard,
  // Renders NOTHING off a show day — see ShowtimeCard. It sits high in every role's stack for that
  // reason: it costs the other 300 days nothing.
  showtime: ShowtimeCard,
  recent_release: RecentReleaseCard,
  opportunities: OpportunitiesCard,
  todo: TodoCard,
  upcoming: UpcomingCard,
  travel: TravelCard,
  my_bunk: MyBunkHomeCard,
  case_checklist: CaseChecklistCard,
  ticket_sales: TicketSalesCard,
  vip_list: VipCard,
  // The consolidated Merch section (ART-182): `merch` = the 4-box full section, `merch_pos` = the POS-only
  // variant for a merch account. The retired merch_cut/pos_sales/merch_qr keys are gone.
  merch: MerchSectionCard,
  merch_pos: MerchPosSectionCard,
  // The Audience section (ART-181) replaced `your_numbers`; the Input List left the home (ART-180).
  audience: AudienceCard,
};

export function MobileHome() {
  const { bandId } = useBandContext();
  const nav = useNavigation(`band:${bandId}`);
  const keys = nav.data?.mobile?.home ?? [];

  return (
    <div className={styles.home}>
      {nav.isLoading && keys.length === 0 ? (
        <HomeSkeleton />
      ) : keys.length === 0 ? (
        // A resolved-but-empty home = a roster/org context (deferred) or a seat with no home widgets granted.
        <EmptyState title="Nothing here yet" description="Your home cards will appear here." />
      ) : (
        keys.map((key) => {
          const Widget = HOME_WIDGETS[key];
          if (!Widget) return null; // unknown key ⇒ render nothing (fail closed; the server list is the source of truth)
          return (
            <WidgetBoundary key={key} widget={key}>
              <Widget />
            </WidgetBoundary>
          );
        })
      )}
    </div>
  );
}

function HomeSkeleton() {
  return (
    <div aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <section key={i} className={styles.card}>
          <Skeleton variant="line" width="40%" />
          <Skeleton variant="block" height={72} />
        </section>
      ))}
    </div>
  );
}

/**
 * A per-widget error boundary: a card that throws during render degrades to a single error tile instead of
 * taking down the whole home stack. Data errors are already handled by each card's four-state contract — this catches
 * the residual render-time crash class.
 */
class WidgetBoundary extends Component<{ widget: string; children: ReactNode }, { failed: boolean }> {
  constructor(props: { widget: string; children: ReactNode }) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override render() {
    if (this.state.failed) {
      return (
        <section className={styles.card}>
          <header className={styles.cardHead}>
            <MEyebrow>{this.props.widget.replace(/_/g, " ")}</MEyebrow>
          </header>
          <EmptyState title="Couldn't show this card" description="Pull to refresh to try again." />
        </section>
      );
    }
    return this.props.children;
  }
}
